import * as React from 'react';
import { UpdateEditingStyleInput } from '../../types';
import { useUpdateEditingStyleMutation } from '../queries/useUpdateEditingStyleMutation';
import { readValidationMessages, ValidationMessage } from '../../../../shared/validationErrors';
import { useTelemetryContext } from '../../../../telemetry/telemetryContext';
import { getUserInfo } from '../../../../telemetry/utils';
import { useUserContext } from '../../../../shared/context/user-context';

export type UpdateEditingStyleSuccess = {
    name: string;
};

export type UpdateEditingStyleFailure = {
    name: string;
    messages: ValidationMessage[];
};

type UpdateEditingStyleState = {
    updateEditingStyle: (id: number, input: UpdateEditingStyleInput) => Promise<void>;
    updateError: UpdateEditingStyleFailure | null;
    dismissUpdateError: () => void;
    updateSuccess: UpdateEditingStyleSuccess | null;
    dismissUpdateSuccess: () => void;
};

export function useUpdateEditingStyle(): UpdateEditingStyleState {
    const { user } = useUserContext();
    const { logger } = useTelemetryContext();
    const requestUpdate = useUpdateEditingStyleMutation();
    const [updateError, setUpdateError] = React.useState<UpdateEditingStyleFailure | null>(null);
    const [updateSuccess, setUpdateSuccess] = React.useState<UpdateEditingStyleSuccess | null>(
        null
    );

    async function updateEditingStyle(id: number, input: UpdateEditingStyleInput): Promise<void> {
        try {
            const savedEditingStyle = await requestUpdate(id, input);
            setUpdateSuccess({ name: savedEditingStyle.name });
        } catch (error) {
            logger.logException(error, {
                origin: `EditingStyles.UpdateEditingStyle Id: ${id}`,
                userInfo: getUserInfo(user)
            });
            setUpdateError({ name: input.name, messages: await readValidationMessages(error) });
        }
    }

    function dismissUpdateError(): void {
        setUpdateError(null);
    }

    function dismissUpdateSuccess(): void {
        setUpdateSuccess(null);
    }

    return {
        updateEditingStyle,
        updateError,
        dismissUpdateError,
        updateSuccess,
        dismissUpdateSuccess
    };
}
